import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Plus, X, DollarSign, ShoppingCart, Settings, Check, AlertTriangle, Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `RD$ ${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminVirtualProductsPage = () => {
  const [kits, setKits] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Dialogs
  const [editPriceKit, setEditPriceKit] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [configKit, setConfigKit] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sellKit, setSellKit] = useState(null);
  const [clientName, setClientName] = useState('');
  const [sellNotes, setSellNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [kRes, pRes] = await Promise.all([
        axios.get(`${API}/operations/virtual-products`),
        axios.get(`${API}/operations/products`),
      ]);
      setKits(kRes.data);
      setProducts(pRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);


  // Price update
  const openEditPrice = (kit) => { setEditPriceKit(kit); setNewPrice(String(kit.sale_price)); };
  const savePrice = async () => {
    if (!newPrice || parseFloat(newPrice) <= 0) { toast.error('Precio inválido'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/operations/virtual-products/${editPriceKit.id}/price`, { sale_price: parseFloat(newPrice) });
      toast.success('Precio actualizado');
      setEditPriceKit(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // Components config
  const openConfig = (kit) => {
    setConfigKit(kit);
    setSelectedIds(kit.components_detail?.map(c => c.product_id) || []);
  };
  const toggleProduct = (pid) => {
    setSelectedIds(prev => {
      if (prev.includes(pid)) return prev.filter(id => id !== pid);
      if (prev.length >= 12) { toast.error('Máximo 12 suplementos'); return prev; }
      return [...prev, pid];
    });
  };
  const saveComponents = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/operations/virtual-products/${configKit.id}/components`, { product_ids: selectedIds });
      toast.success(`${selectedIds.length} suplementos configurados`);
      setConfigKit(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // Sell kit
  const openSell = (kit) => { setSellKit(kit); setClientName(''); setSellNotes(''); };
  const executeSell = async () => {
    if (!clientName.trim()) { toast.error('Ingresa el nombre del cliente'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/operations/virtual-products/${sellKit.id}/sell`, { client_name: clientName, notes: sellNotes });
      toast.success(`${res.data.message} — Orden: ${res.data.order_number}`);
      setSellKit(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al vender'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-virtual-products-page">
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Productos Virtuales</h1>
        <p className="text-gray-500 mt-1">Kits compuestos por suplementos del inventario</p>
      </div>

      {/* Kit Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {kits.map(kit => {
          const hasComponents = kit.components_detail?.length > 0;
          const allInStock = hasComponents && kit.components_detail.every(c => c.available_qty >= kit.capsules_per_product);
          return (
            <Card key={kit.id} className="border border-gray-200 shadow-sm overflow-hidden" data-testid={`kit-card-${kit.type}`}>
              {/* Kit Header */}
              <div className={`p-5 text-white ${kit.type === 'semanal' ? 'bg-gradient-to-r from-blue-600 to-blue-800' : 'bg-gradient-to-r from-violet-600 to-violet-800'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                      <Box size={22} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{kit.name}</h2>
                      <p className="text-white/70 text-xs">{kit.capsules_per_product} cápsulas × {kit.components_detail?.length || 0} suplementos</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{fmt(kit.sale_price)}</p>
                    <button onClick={() => openEditPrice(kit)} className="text-xs text-white/60 hover:text-white underline">Cambiar precio</button>
                  </div>
                </div>
              </div>

              <CardContent className="p-5 space-y-4">
                {/* Cost/Profit Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-400">Costo</p>
                    <p className="font-bold text-gray-800 text-sm">{fmt(kit.computed_cost)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-400">Ganancia</p>
                    <p className={`font-bold text-sm ${kit.computed_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(kit.computed_profit)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-400">Margen</p>
                    <p className="font-bold text-emerald-600 text-sm">
                      {kit.sale_price > 0 ? Math.round((kit.computed_profit / kit.sale_price) * 100) : 0}%
                    </p>
                  </div>
                </div>

                {/* Components */}
                {hasComponents ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Composición ({kit.components_detail.length}/12 suplementos)</p>
                    <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                      {kit.components_detail.map(c => (
                        <div key={c.product_id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="font-mono text-[10px] flex-shrink-0">{c.sku}</Badge>
                            <span className="text-gray-700 truncate">{c.name}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs text-gray-400">{c.capsules} cáps</span>
                            <Badge className={`text-[10px] ${c.available_qty >= c.capsules ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              Disp: {c.available_qty}
                            </Badge>
                            <span className="text-xs font-medium text-gray-500">{fmt(c.subtotal_cost)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="py-6 text-center text-gray-400">
                    <Package size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Sin suplementos configurados</p>
                  </div>
                )}

                {/* Stock Warning */}
                {hasComponents && !allInStock && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    <AlertTriangle size={14} /> Algunos suplementos no tienen inventario suficiente
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => openConfig(kit)} className="flex-1 text-sm" data-testid={`config-${kit.type}`}>
                    <Settings size={15} className="mr-1.5" /> Configurar
                  </Button>
                  <Button onClick={() => openSell(kit)} disabled={!hasComponents || !allInStock}
                    className="flex-1 text-sm bg-[#467322] hover:bg-[#3A601C] text-white"
                    data-testid={`sell-${kit.type}`}>
                    <ShoppingCart size={15} className="mr-1.5" /> Vender Kit
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Price Dialog */}
      <Dialog open={!!editPriceKit} onOpenChange={() => setEditPriceKit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Actualizar Precio — {editPriceKit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs font-semibold text-gray-600">Precio de Venta (RD$)</Label>
            <Input type="number" step="0.01" value={newPrice} onChange={e => setNewPrice(e.target.value)} data-testid="input-new-price" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPriceKit(null)}>Cancelar</Button>
            <Button onClick={savePrice} disabled={saving} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="save-price-btn">
              {saving ? 'Guardando...' : 'Actualizar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Components Dialog */}
      <Dialog open={!!configKit} onOpenChange={() => setConfigKit(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Configurar Suplementos — {configKit?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">Selecciona hasta 12 suplementos del inventario ({selectedIds.length}/12)</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto mt-2">
            {products.map(p => {
              const isSelected = selectedIds.includes(p.id);
              return (
                <button key={p.id} onClick={() => toggleProduct(p.id)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    isSelected ? 'border-[#467322] bg-[#467322]/5' : 'border-gray-100 hover:border-gray-200'
                  }`} data-testid={`toggle-${p.sku}`}>
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">{p.sku}</Badge>
                      <span className="font-medium text-sm text-gray-800 truncate">{p.name}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Costo: ${p.unit_cost} · Disponible: {p.quantity}</p>
                  </div>
                  {isSelected && <Check size={16} className="text-[#467322] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigKit(null)}>Cancelar</Button>
            <Button onClick={saveComponents} disabled={saving || selectedIds.length === 0}
              className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="save-components-btn">
              {saving ? 'Guardando...' : `Guardar (${selectedIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell Kit Dialog */}
      <Dialog open={!!sellKit} onOpenChange={() => setSellKit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Vender {sellKit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Precio de Venta:</span><span className="font-bold text-[#467322]">{fmt(sellKit?.sale_price)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Costo:</span><span className="font-medium">{fmt(sellKit?.computed_cost)}</span></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span className="text-gray-500">Ganancia:</span><span className="font-bold text-emerald-600">{fmt(sellKit?.computed_profit)}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Nombre del Cliente *</Label>
              <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nombre del cliente" data-testid="sell-client-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Notas</Label>
              <Input value={sellNotes} onChange={e => setSellNotes(e.target.value)} placeholder="Notas adicionales (opcional)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSellKit(null)}>Cancelar</Button>
            <Button onClick={executeSell} disabled={saving} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="confirm-sell-btn">
              {saving ? 'Procesando...' : 'Confirmar Venta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVirtualProductsPage;
